import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Search, Star, ExternalLink, Mail, Filter, Building2, Users as UsersIcon, Wrench, Award, Factory } from 'lucide-react';
import ToolSelector from '../components/ToolSelector';
import PageTutorial from '../components/PageTutorial';
import BrandMark from '../components/BrandMark';

/**
 * Community Directory — vetted vendors, consultants, service providers,
 * associations and manufacturers. Public route (also accessible without login).
 */
const CATEGORIES = [
  { key: 'all',           label: 'All',            icon: Filter },
  { key: 'vendor',        label: 'Vendors',        icon: Building2, desc: 'Suppliers of trays, mylar, oxygen absorbers and consumables.' },
  { key: 'consultant',    label: 'Consultants',    icon: UsersIcon, desc: 'People you can hire for guidance, recipe work or scaling.' },
  { key: 'service',       label: 'Services',       icon: Wrench,    desc: 'Repair, calibration, cycle analysis, contract drying.' },
  { key: 'manufacturer',  label: 'Manufacturers',  icon: Factory,   desc: 'Machine manufacturers and OEM parts.' },
  { key: 'association',   label: 'Associations',   icon: Award,     desc: 'Industry groups, standards and community organizations.' },
];

const Directory = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');

  const [applyOpen, setApplyOpen] = useState(false);
  const [applySubmitting, setApplySubmitting] = useState(false);
  const [applyForm, setApplyForm] = useState({
    business_name: '',
    contact_name: '',
    contact_email: '',
    website: '',
    region: '',
    category: 'vendor',
    services: '',
    description: '',
  });

  const submitApplication = async () => {
    if (!applyForm.business_name || !applyForm.contact_email || !applyForm.description) {
      toast.error('Business name, email and description are required');
      return;
    }
    setApplySubmitting(true);
    try {
      await axios.post(`${API}/directory/apply`, {
        ...applyForm,
        services: applyForm.services.split(',').map(s => s.trim()).filter(Boolean),
      });
      toast.success("Application received. We'll review and get back to you.");
      setApplyOpen(false);
      setApplyForm({
        business_name: '', contact_name: '', contact_email: '', website: '',
        region: '', category: 'vendor', services: '', description: '',
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit application');
    } finally {
      setApplySubmitting(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/partners`);
        setPartners(res.data || []);
      } catch (e) {
        setPartners([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [API]);

  const counts = useMemo(() => {
    const c = { all: partners.length };
    for (const k of CATEGORIES.slice(1).map(c => c.key)) {
      c[k] = partners.filter(p => (p.category || 'vendor') === k).length;
    }
    return c;
  }, [partners]);

  const filtered = useMemo(() => {
    let list = partners;
    if (category !== 'all') list = list.filter(p => (p.category || 'vendor') === category);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(p =>
        p.name?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q) ||
        p.region?.toLowerCase().includes(q) ||
        (p.services || []).some(s => s.toLowerCase().includes(q))
      );
    }
    // Featured first, then alpha
    return list.slice().sort((a, b) => {
      if (a.featured !== b.featured) return a.featured ? -1 : 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [partners, query, category]);

  const activeMeta = CATEGORIES.find(c => c.key === category);

  return (
    <div className="min-h-screen bg-background" data-testid="directory-page">
      {/* Top nav */}
      <header className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <Button
            variant="ghost" size="sm"
            className="-ml-3 font-semibold"
            onClick={() => navigate(user ? '/dashboard' : '/')}
            data-testid="directory-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> {user ? 'Dashboard' : 'Home'}
          </Button>
          <BrandMark size="md" onClick={() => navigate(user ? '/dashboard' : '/')} />
          <div className="w-[120px]" />
        </div>
      </header>

      <ToolSelector />

      {/* Hero */}
      <section className="bg-navy-spotlight text-background">
        <div className="container mx-auto px-6 pt-14 pb-16 text-center">
          <span className="pill-badge">Community Directory</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-6 text-balance">
            Vetted vendors, consultants
            <br />
            <span className="text-amber-brand">and service providers.</span>
          </h1>
          <p className="mt-5 max-w-2xl mx-auto text-background/70 leading-relaxed">
            A shortcut past the marketplace noise. Every listing here has been reviewed by the
            academy team or a trusted operator.
          </p>

          {/* Search */}
          <div className="mt-8 max-w-xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-background/50" />
              <Input
                placeholder="Search by name, service or region…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-11 h-12 rounded-full bg-background/10 border-background/20 text-background placeholder:text-background/50 focus-visible:ring-primary"
                data-testid="directory-search"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Category tabs */}
      <div className="border-b border-border sticky top-16 bg-background/95 backdrop-blur z-20">
        <div className="container mx-auto px-6 overflow-x-auto no-scrollbar">
          <div className="flex gap-1 py-2 min-w-max">
            {CATEGORIES.map(c => {
              const Icon = c.icon;
              const active = category === c.key;
              return (
                <button
                  key={c.key}
                  onClick={() => setCategory(c.key)}
                  data-testid={`directory-cat-${c.key}`}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition ${
                    active
                      ? 'bg-navy text-background'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {c.label}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                    active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}>
                    {counts[c.key] ?? 0}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Category description */}
      {activeMeta?.desc && (
        <div className="container mx-auto px-6 pt-6">
          <p className="text-sm text-muted-foreground max-w-2xl">
            <span className="eyebrow mr-2">{activeMeta.label}</span>
            {activeMeta.desc}
          </p>
        </div>
      )}

      {/* Grid */}
      <main className="container mx-auto px-6 py-10">
        <PageTutorial id="directory" />
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="pro-card p-6 animate-pulse">
                <div className="h-4 w-1/2 bg-muted rounded mb-3" />
                <div className="h-3 w-full bg-muted rounded mb-2" />
                <div className="h-3 w-3/4 bg-muted rounded" />
                <div className="h-10 w-full bg-muted rounded mt-6" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="pro-card p-14 text-center max-w-lg mx-auto">
            <div className="h-14 w-14 rounded-2xl bg-muted mx-auto flex items-center justify-center mb-4">
              <Search className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="font-heading text-xl font-bold">No listings yet</p>
            <p className="text-sm text-muted-foreground mt-1.5">
              {query
                ? 'Try a different search or clear the category filter.'
                : 'This section is being curated. Check back soon.'}
            </p>
            {(query || category !== 'all') && (
              <Button
                variant="outline"
                className="mt-5 rounded-full font-semibold"
                onClick={() => { setQuery(''); setCategory('all'); }}
                data-testid="directory-reset-btn"
              >
                Reset filters
              </Button>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
            {filtered.map((p, i) => (
              <article
                key={p.id}
                className="pro-card-interactive p-6 flex flex-col animate-fade-up"
                style={{ '--i': Math.min(i, 10) }}
                data-testid={`directory-card-${p.id}`}
              >
                <div className="flex items-start gap-3">
                  {p.logo ? (
                    <img src={p.logo} alt={p.name} className="h-12 w-12 rounded-lg object-cover border border-border flex-shrink-0" />
                  ) : (
                    <div className="h-12 w-12 rounded-lg bg-navy text-primary flex items-center justify-center font-heading text-lg font-extrabold flex-shrink-0">
                      {p.name?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <h3 className="font-heading text-lg font-bold tracking-tight truncate">{p.name}</h3>
                      {p.featured && (
                        <span title="Featured" className="text-primary">
                          <Star className="w-4 h-4 fill-primary" />
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {p.region || 'Region not specified'}
                      {p.category && p.category !== 'vendor' && (
                        <> · <span className="capitalize">{p.category}</span></>
                      )}
                    </div>
                  </div>
                </div>

                {p.description && (
                  <p className="mt-4 text-sm text-muted-foreground leading-relaxed line-clamp-3">
                    {p.description}
                  </p>
                )}

                {p.services && p.services.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {p.services.slice(0, 4).map(s => (
                      <span key={s} className="text-[11px] font-semibold px-2 py-1 rounded-full bg-muted text-muted-foreground">
                        {s}
                      </span>
                    ))}
                    {p.services.length > 4 && (
                      <span className="text-[11px] font-semibold px-2 py-1 rounded-full bg-muted text-muted-foreground">
                        +{p.services.length - 4}
                      </span>
                    )}
                  </div>
                )}

                <div className="flex-1" />
                <div className="mt-5 flex gap-2">
                  {p.link && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-full font-semibold flex-1"
                      onClick={() => window.open(p.link, '_blank', 'noopener,noreferrer')}
                      data-testid={`directory-visit-${p.id}`}
                    >
                      Visit <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                    </Button>
                  )}
                  {p.contact_email && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-full font-semibold"
                      onClick={() => window.open(`mailto:${p.contact_email}`, '_self')}
                      data-testid={`directory-email-${p.id}`}
                    >
                      <Mail className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}

        {/* CTA to be listed */}
        <div className="mt-16 pro-card bg-navy text-background p-8 sm:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border-0">
          <div>
            <span className="pill-badge">Get listed</span>
            <h3 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mt-3">
              Run a vendor or consulting shop?
            </h3>
            <p className="mt-2 text-background/70 max-w-xl">
              Submit your business for review — we vet every listing before it goes live.
            </p>
          </div>
          <Button
            size="lg"
            className="rounded-full h-12 px-6 font-bold bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={() => setApplyOpen(true)}
            data-testid="directory-apply-btn"
          >
            Apply to be listed
          </Button>
        </div>
      </main>

      {/* Apply dialog */}
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <span className="eyebrow">Get listed</span>
            <DialogTitle className="font-heading text-2xl font-bold mt-2 tracking-tight">
              Apply to the directory
            </DialogTitle>
            <DialogDescription>
              We vet every listing before it goes live. Reviews usually take 3–5 business days.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Business name *</Label>
                <Input
                  value={applyForm.business_name}
                  onChange={(e) => setApplyForm({ ...applyForm, business_name: e.target.value })}
                  placeholder="Your business"
                  data-testid="apply-business-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Category *</Label>
                <Select
                  value={applyForm.category}
                  onValueChange={(v) => setApplyForm({ ...applyForm, category: v })}
                >
                  <SelectTrigger data-testid="apply-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.slice(1).map(c => (
                      <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Contact name</Label>
                <Input
                  value={applyForm.contact_name}
                  onChange={(e) => setApplyForm({ ...applyForm, contact_name: e.target.value })}
                  placeholder="Your name"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Contact email *</Label>
                <Input
                  type="email"
                  value={applyForm.contact_email}
                  onChange={(e) => setApplyForm({ ...applyForm, contact_email: e.target.value })}
                  placeholder="you@business.com"
                  data-testid="apply-email"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Website</Label>
                <Input
                  value={applyForm.website}
                  onChange={(e) => setApplyForm({ ...applyForm, website: e.target.value })}
                  placeholder="https://…"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Region</Label>
                <Input
                  value={applyForm.region}
                  onChange={(e) => setApplyForm({ ...applyForm, region: e.target.value })}
                  placeholder="e.g. Pacific NW, USA"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Services (comma-separated)</Label>
              <Input
                value={applyForm.services}
                onChange={(e) => setApplyForm({ ...applyForm, services: e.target.value })}
                placeholder="e.g. Mylar bags, oxygen absorbers, wholesale"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Description *</Label>
              <textarea
                value={applyForm.description}
                onChange={(e) => setApplyForm({ ...applyForm, description: e.target.value })}
                placeholder="What you do, who you serve, and how you're different."
                className="w-full p-3 border border-border rounded-xl bg-transparent min-h-[100px] focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="apply-description"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" className="rounded-full font-semibold" onClick={() => setApplyOpen(false)}>Cancel</Button>
            <Button
              className="rounded-full font-semibold bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={submitApplication}
              disabled={applySubmitting}
              data-testid="apply-submit"
            >
              {applySubmitting ? 'Submitting…' : 'Submit application'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Directory;
